import React, { useState, useEffect } from 'react';
import { zApiAdminAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useTheme } from '../../contexts/ThemeContext';
import Layout from '../../components/Layout';
import PageHeader from '../../components/PageHeader';
import { Plus, RefreshCw, Server, Loader2 } from 'lucide-react';

const ZApiInstances = () => {
  const { darkMode } = useTheme();
  const [instancias, setInstancias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [criando, setCriando] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ nome: '', businessDevice: true });

  const loadInstancias = async () => {
    try {
      const res = await zApiAdminAPI.listar();
      setInstancias(res.data);
    } catch (error) {
      toast.error('Erro ao carregar instâncias');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadInstancias(); }, []);

  const handleCriar = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) return toast.warning('Informe o nome');
    
    setCriando(true);
    try {
      await zApiAdminAPI.criar(form);
      toast.success('Instância criada!');
      setShowModal(false);
      setForm({ nome: '', businessDevice: true });
      loadInstancias();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao criar instância');
    } finally {
      setCriando(false);
    }
  };

  const handleAssinar = async (id) => {
    try {
      await zApiAdminAPI.assinar(id);
      toast.success('Instância assinada!');
      loadInstancias();
    } catch (error) {
      toast.error('Erro ao assinar instância');
    }
  };

  const getStatusBadge = (inst) => {
    if (inst.connected) {
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Online</span>;
    }
    const styles = {
      disponivel: 'bg-yellow-100 text-yellow-800',
      em_uso: 'bg-blue-100 text-blue-800',
      expirada: 'bg-red-100 text-red-800',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[inst.status] || styles.disponivel}`}>
        {inst.connected === false ? 'Offline' : inst.status}
      </span>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center p-8"><Loader2 className="animate-spin" /></div>
      </Layout>
    );
  }

  return (
    <Layout>
    <div className="space-y-6">
      <PageHeader title="Pool de Instâncias Z-API" subtitle="Gerencie instâncias Z-API compartilhadas">
          <button onClick={loadInstancias} className="p-2 rounded hover:bg-gray-200 dark:hover:bg-zinc-700">
            <RefreshCw size={20} />
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-1 sm:gap-2 bg-green-600 text-white px-2 py-1.5 sm:px-4 sm:py-2 rounded hover:bg-green-700"
          >
            <Plus size={20} /> <span className="hidden sm:inline">Nova Instância</span>
          </button>
      </PageHeader>

    <div className={`p-6 rounded-xl ${darkMode ? 'bg-zinc-800 text-white' : 'bg-white'}`}>

      <div className="overflow-x-auto">
        <table className={`w-full ${darkMode ? 'text-gray-200' : ''}`}>
          <thead className={darkMode ? 'bg-zinc-700' : 'bg-gray-100'}>
            <tr>
              <th className="p-3 text-left">Nome</th>
              <th className="p-3 text-left">Cliente</th>
              <th className="p-3 text-left">Msgs/Mês</th>
              <th className="p-3 text-left">Custo</th>
              <th className="p-3 text-left">Vencimento</th>
              <th className="p-3 text-left">Criado em</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-left">Ações</th>
            </tr>
          </thead>
          <tbody>
            {instancias.map((inst) => (
              <tr key={inst.id} className={`border-b ${darkMode ? 'border-zinc-600' : 'border-gray-200'}`}>
                <td className="p-3">{inst.nome}</td>
                <td className="p-3">{inst.cliente_nome || <span className="text-gray-400">-</span>}</td>
                <td className="p-3">
                  <span className="font-medium">{inst.mensagens_mes || 0}</span>
                </td>
                <td className="p-3">
                  {inst.custo_mensal ? `R$ ${Number(inst.custo_mensal).toFixed(2)}` : '-'}
                </td>
                <td className="p-3">
                  {inst.vencimento ? new Date(inst.vencimento).toLocaleDateString('pt-BR') : '-'}
                  {inst.payment_status && (
                    <span className={`ml-2 text-xs ${inst.payment_status === 'PAID' ? 'text-green-600' : 'text-red-600'}`}>
                      ({inst.payment_status})
                    </span>
                  )}
                </td>
                <td className="p-3 text-sm text-gray-500">
                  {new Date(inst.created_at).toLocaleDateString('pt-BR')}
                </td>
                <td className="p-3">{getStatusBadge(inst)}</td>
                <td className="p-3">
                  {!inst.assinada && inst.status === 'disponivel' && (
                    <button
                      onClick={() => handleAssinar(inst.id)}
                      className="text-blue-600 hover:underline text-sm"
                    >
                      Assinar
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {instancias.length === 0 && (
              <tr>
                <td colSpan={8} className="p-8 text-center text-gray-500">
                  Nenhuma instância no pool
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`p-6 rounded-lg w-full max-w-md ${darkMode ? 'bg-zinc-800' : 'bg-white'}`}>
            <h3 className="text-lg font-bold mb-4">Criar Instância Z-API</h3>
            <form onSubmit={handleCriar}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Nome da Instância</label>
                <input
                  type="text"
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  className={`w-full p-2 border rounded ${darkMode ? 'bg-zinc-700 border-zinc-600' : 'border-gray-300'}`}
                  placeholder="Ex: Cliente XYZ"
                />
              </div>
              <div className="mb-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.businessDevice}
                    onChange={(e) => setForm({ ...form, businessDevice: e.target.checked })}
                  />
                  <span className="text-sm">WhatsApp Business</span>
                </label>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 rounded border"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={criando}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {criando && <Loader2 className="animate-spin" size={16} />}
                  Criar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </div>
    </Layout>
  );
};

export default ZApiInstances;
